import React, { useEffect, useState } from 'react';
import { Camera, ChevronDown, Loader2 } from 'lucide-react';
import Evidence360Modal from './Evidence360Modal';
import { sw } from '../../lib/sitewalkApi';

/**
 * "360 Evidence" button for downstream detail views (Activity / BOQ / BIM / Issue / Quality / Safety).
 * Fetches authorized reverse-evidence for the record and opens the exact panorama on selection.
 * Renders nothing when there is no evidence (keeps detail views clean). Backend enforces access.
 */
export default function EvidenceButton({ projectId, kind, id, obsKind, compact = false, label = '360 Evidence' }) {
  const [items, setItems] = useState(null);   // [{title, params}]
  const [open, setOpen] = useState(false);
  const [modalParams, setModalParams] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) { setLoading(false); return; }
    let alive = true;
    const done = (list) => { if (alive) { setItems(list); setLoading(false); } };
    const fail = () => { if (alive) { setItems([]); setLoading(false); } };
    (async () => {
      try {
        if (kind === 'activity') {
          const d = await sw.evidenceActivity(projectId, id);
          done((d.evidence || []).map((e) => ({ title: `${e.walkNumber || 'Walk'} · ${e.capturePoint?.name || ''} · ${(e.capturePoint?.capturedAt || '').slice(0, 10)}`, params: { capturePointId: e.capturePoint?.id } })));
        } else if (kind === 'boq-item') {
          const d = await sw.evidenceBoqItem(projectId, id);
          done((d.evidence || []).map((e) => ({ title: `Hotspot ${e.type} · capture ${(e.capturePointId || '').slice(0, 6)}`, params: { hotspotId: e.hotspotId } })));
        } else if (kind === 'bim-element') {
          const d = await sw.evidenceBimElement(projectId, id);
          const caps = (d.captures || []).map((e) => ({ title: `${e.walkNumber || 'Walk'} · ${e.capturePoint?.name || ''}`, params: { capturePointId: e.capturePoint?.id } }));
          const hss = (d.hotspots || []).map((e) => ({ title: `Hotspot ${e.type}`, params: { hotspotId: e.hotspotId } }));
          done([...caps, ...hss]);
        } else if (kind === 'issue') {
          const d = await sw.evidenceIssue(projectId, id);
          done(d.hasEvidence && d.chain ? [{ title: `${d.chain.siteWalk?.walkNumber || 'Walk'} · exact hotspot`, params: { hotspotId: d.chain.hotspot?.id, capturePointId: d.chain.capturePoint?.id, panoramaId: d.chain.panorama?.id } }] : []);
        } else if (kind === 'observation') {
          const d = await sw.evidenceObservation(projectId, obsKind, id);
          done((d.evidence || []).map((e) => ({ title: `Hotspot ${e.type}`, params: { hotspotId: e.hotspotId } })));
        } else { fail(); }
      } catch (e) { fail(); }
    })();
    return () => { alive = false; };
  }, [projectId, kind, id, obsKind]);

  if (loading) return <span className="inline-flex items-center gap-1 text-xs text-slate-400" data-testid="evidence-btn-loading"><Loader2 className="w-3.5 h-3.5 animate-spin" /></span>;
  if (!items || items.length === 0) return null;

  const single = items.length === 1;
  const onClick = () => { if (single) setModalParams(items[0].params); else setOpen((v) => !v); };

  return (
    <div className="relative inline-block">
      <button onClick={onClick} data-testid={`evidence-btn-${kind}`}
        className={`inline-flex items-center gap-1.5 rounded-lg border border-sky-300 text-sky-700 hover:bg-sky-50 ${compact ? 'px-2 py-1 text-[11px]' : 'px-3 py-1.5 text-xs'}`}>
        <Camera className={compact ? 'w-3.5 h-3.5' : 'w-4 h-4'} /> {label} ({items.length})
        {!single && <ChevronDown className="w-3 h-3" />}
      </button>
      {open && !single && (
        <div className="absolute right-0 mt-1 w-64 max-h-64 overflow-y-auto bg-white rounded-lg shadow-xl border border-slate-200 z-30 p-1" data-testid={`evidence-menu-${kind}`}>
          {items.map((it, i) => (
            <button key={i} onClick={() => { setModalParams(it.params); setOpen(false); }}
              className="w-full text-left px-2.5 py-2 rounded-md hover:bg-slate-50 text-xs text-slate-600" data-testid={`evidence-item-${kind}-${i}`}>
              {it.title}
            </button>
          ))}
        </div>
      )}
      {modalParams && <Evidence360Modal projectId={projectId} params={modalParams} onClose={() => setModalParams(null)} />}
    </div>
  );
}
